import { TextareaAutosize } from '@newfront-insurance/core-ui';
import { Button, Dialog, DialogContent, DialogTitle, DialogDescription, cn } from '@newfront-insurance/core-ui/v2';
import { ArrowRight } from 'lucide-react';
import { useState } from 'react';

import { AnalyticsPagination } from './analytics-table';
import { PROPOSAL_PREVIEW_ITEMS, REVIEW_PREVIEW_ITEMS } from './preview-fixtures';
import { useChatOverlayContext } from '../../contexts/chat-overlay-context';
import { novaGradientClass } from '../../design-system';

export function AskNovaAnalytics() {
  const [question, setQuestion] = useState('');
  const { startNewChatWithInput, setIsSettingsOpen } = useChatOverlayContext();
  return (
    <section className="bg-steel-50 flex flex-col gap-4 rounded-sm p-4" aria-label="Ask Nova about this data">
      <div>
        <h3 className="text-steel-500 text-lg leading-6 font-semibold">Ask Nova about this data</h3>
        <p className="text-steel-400 mt-1 text-sm leading-4">Ask about people, skills or usage.</p>
      </div>
      <form
        className={cn('rounded-[12px] p-0.5', novaGradientClass.aiBorderIcon)}
        onSubmit={(event) => {
          event.preventDefault();
          const prompt = question.trim();
          if (!prompt) return;
          setIsSettingsOpen(false);
          startNewChatWithInput(prompt, { submitImmediately: false });
        }}
      >
        <div className="flex min-h-[100px] flex-col gap-2 rounded-[10px] bg-white p-3">
          <TextareaAutosize
            data-testid="nova-analytics-question"
            aria-label="Question for Nova"
            aria-describedby="nova-analytics-chat-help"
            placeholder="e.g. What is nobody building a skill for?"
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            minRows={1}
            maxRows={5}
            maxLength={4000}
            className="text-steel-500 min-h-6 border-0 bg-white p-0 leading-5 shadow-none"
          />
          <div className="flex justify-end">
            <Button
              data-testid="nova-analytics-ask-in-chat"
              type="submit"
              disabled={!question.trim()}
              className="disabled:border-steel-100 disabled:bg-steel-100 disabled:text-steel-200 h-9 gap-2 rounded-full px-4 py-2"
            >
              <ArrowRight className="size-[18px]" aria-hidden="true" />
              Ask in chat
            </Button>
          </div>
        </div>
      </form>
      <p id="nova-analytics-chat-help" className="text-steel-400 text-xs leading-4">
        Opens a new chat. Sample dashboard data isn’t connected to Nova yet.
      </p>
    </section>
  );
}

const BADGE_CLASSES = {
  Duplication: 'bg-brand-100 text-brand-400',
  Replaced: 'bg-fire-100 text-[#B27855]',
  Abandoned: 'bg-fire-100 text-fire-500',
};

export function NovaAnalyticsReviewPreviews({ teamId }: { teamId: string | null }) {
  const [offset, setOffset] = useState(0);
  const [review, setReview] = useState<{ title: string; detail: string } | null>(null);
  const items = REVIEW_PREVIEW_ITEMS.filter((item) => !teamId || item.teamId === teamId);
  const proposals = PROPOSAL_PREVIEW_ITEMS.filter((item) => !teamId || item.teamId === teamId);
  const limit = 6;
  return (
    <>
      <section data-testid="nova-analytics-reviews" className="flex flex-col gap-3" aria-label="Skills to review">
        <div>
          <h3 className="text-steel-500 text-lg leading-6 font-semibold">Skills to review</h3>
          <p className="text-steel-400 mt-1 text-sm leading-4">Skills duplicated, replaced, or abandoned</p>
        </div>
        <div className="border-steel-200/50 overflow-hidden rounded-sm border">
          <ul className="divide-steel-200/50 divide-y">
            {items.length === 0 ? (
              <li data-testid="nova-analytics-reviews-empty" className="text-steel-400 px-4 py-8 text-center text-sm">
                No sample reviews for this team.
              </li>
            ) : null}
            {items.slice(offset, offset + limit).map((item) => (
              <li key={item.id} className="flex flex-wrap items-center gap-3 px-4 py-2">
                <span className={cn('rounded-md px-2 py-1 text-sm leading-4', BADGE_CLASSES[item.kind])}>
                  {item.kind}
                </span>
                <div className="min-w-[160px] flex-1">
                  <p className="text-steel-500 text-base leading-5 font-semibold">{item.title}</p>
                  <p className="text-steel-400 mt-1 text-sm leading-4">{item.detail}</p>
                </div>
                <Button
                  data-testid="nova-analytics-review-item"
                  type="button"
                  variant="secondary"
                  className="h-8 px-8 py-1"
                  aria-label={`Review ${item.title}`}
                  onClick={() => setReview({ title: item.title, detail: item.detail })}
                >
                  Review
                </Button>
              </li>
            ))}
          </ul>
          <AnalyticsPagination
            label="Skills to review"
            testId="nova-analytics-reviews"
            offset={offset}
            limit={limit}
            totalCount={items.length}
            onPageChange={setOffset}
          />
        </div>
      </section>

      <section
        data-testid="nova-analytics-proposals"
        className="flex flex-col gap-3"
        aria-label="Proposed by your team"
      >
        <h3 className="text-steel-500 text-lg leading-6 font-semibold">Proposed by your team</h3>
        <ul className="border-steel-200/50 divide-steel-200/50 divide-y overflow-hidden rounded-sm border">
          {proposals.length === 0 ? (
            <li data-testid="nova-analytics-proposals-empty" className="text-steel-400 px-4 py-8 text-center text-sm">
              No sample proposals for this team.
            </li>
          ) : null}
          {proposals.map((proposal) => (
            <li key={proposal.id} className="flex flex-wrap items-center gap-4 px-4 py-2">
              <div className="min-w-[200px] flex-1">
                <p className="text-steel-500 text-base leading-5">
                  <span className="font-semibold">{proposal.person}</span> wants to {proposal.action}{' '}
                  <button
                    data-testid="nova-analytics-proposal-skill"
                    type="button"
                    className="text-brand-400 font-semibold hover:underline"
                    onClick={() => setReview({ title: proposal.skill, detail: proposal.detail })}
                  >
                    {proposal.skill}
                  </button>
                </p>
                <p className="text-steel-400 mt-1 text-sm leading-4">“{proposal.detail}”</p>
              </div>
              <Button
                data-testid="nova-analytics-review-proposal"
                type="button"
                variant="secondary"
                className="h-8 px-8 py-1"
                aria-label={`Review proposal from ${proposal.person}`}
                onClick={() => setReview({ title: proposal.skill, detail: proposal.detail })}
              >
                Review
              </Button>
            </li>
          ))}
        </ul>
      </section>

      <Dialog
        open={Boolean(review)}
        onOpenChange={(open) => {
          if (!open) setReview(null);
        }}
      >
        <DialogContent data-testid="nova-analytics-review-dialog" className="sm:max-w-lg">
          <DialogTitle>{review?.title}</DialogTitle>
          <DialogDescription>{review?.detail}</DialogDescription>
          <p className="text-steel-400 text-sm">This is a sample review. No skill will be changed.</p>
          <Button
            data-testid="nova-analytics-close-review"
            type="button"
            variant="secondary"
            className="justify-self-end"
            onClick={() => setReview(null)}
          >
            Close preview
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
